import { useState, useEffect } from 'react';
import {
  Save,
  FolderOpen,
  Download,
  Upload,
  FileText,
  Sun,
  Moon,
  Menu,
  Settings,
  Plus,
  Search,
  Replace,
  PanelRightClose,
  PanelRightOpen,
  Database,
  FileText as DescriptionsOn,
  FileX as DescriptionsOff,
  Network,
  BookText,
  Type,
  Braces,
  ShieldCheck,
  MousePointerClick,
  Hand,
  Undo,
  Library,
  GitBranch,
  GitCommit,
  LogOut,
  User,
  ChevronDown,
  HelpCircle,
  Bot,
  Filter,
  KeyRound,
  UserX,
} from 'lucide-react';
import useFlowStore from '../stores/useFlowStore';
import { modelApi, domainApi, versionApi, userApi, userTypeApi } from '../utils/api';
import { logger } from '../utils/logger';
import { downloadJSON, parseJSONFile, validateModelData } from '../utils/helpers';
import { parseSQLDDL } from '../utils/sqlParser';
import { generateId } from '../utils/helpers';
import { downloadXMI } from '../utils/xmiExport';
import { importFromXMI, validateXMI } from '../utils/xmiImport';
import { downloadSQL } from '../utils/sqlExport';
import { downloadMermaid } from '../utils/mermaidExport';
import { applyLayout } from '../utils/layoutAlgorithms';
import { applyCase } from '../utils/caseConverter';
import { validateModel } from '../utils/modelValidator';
import { exportToPng, exportToJpeg, exportToSvgImage } from '../utils/exportImage';
import toast from 'react-hot-toast';
import NewModelModal from './modals/NewModelModal';
import LoadModelModal from './modals/LoadModelModal';
import FindReplaceModal from './modals/FindReplaceModal';
import FilterEntitiesModal from './modals/FilterEntitiesModal';
import ConfirmDialog from './modals/ConfirmDialog';
import ExportFormatModal from './modals/ExportFormatModal';
import LayoutAlgorithmModal from './modals/LayoutAlgorithmModal';
import CaseConverterModal from './modals/CaseConverterModal';
import ModelValidatorModal from './modals/ModelValidatorModal';
import DomainsLibraryModal from './modals/DomainsLibraryModal';
import UserTypesModal from './modals/UserTypesModal';
import ModelBusinessRulesModal from './modals/ModelBusinessRulesModal';
import CreateVersionModal from './modals/CreateVersionModal';
import VersionHistoryModal from './modals/VersionHistoryModal';
import ImportSelectorModal from './modals/ImportSelectorModal';
import AiAssistantModal from './modals/AiAssistantModal';
import ChangePasswordModal from './modals/ChangePasswordModal';
import DeleteAccountModal from './modals/DeleteAccountModal';
import { useConfirm } from '../hooks/useConfirm';
import { useAuth } from '../contexts/AuthContext';
import schemaGraphosLogo from '../assets/images/schema_graphos_logo.png';

const Toolbar = ({ onToggleSidebar, onToggleProperties, onToggleSearch, showProperties }) => {
  const {
    modelName,
    modelType,
    currentModel,
    isDirty,
    theme,
    showFieldDescriptions,
    isMultiSelectMode,
    nodes,
    edges,
    selectedNode,
    toggleTheme,
    toggleFieldDescriptions,
    toggleMultiSelectMode,
    getModelData,
    setCurrentModel,
    setIsDirty,
    addNode,
    updateNode,
    onConnect,
    createEdgeDirectly,
    setNodes,
    setSelectedNode,
    undo,
    canUndo,
    userTypes,
    setUserTypes,
    modelBusinessRules,
    setModelBusinessRules,
    globalDomains,
    addGlobalDomain,
    diagramFilter,
  } = useFlowStore();


  const { user, logout, isAdmin, userHasRole } = useAuth();

  // License expiration helpers
  const licenseStatus = (() => {
    const raw = user?.expireDate;
    if (!raw) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exp = new Date(raw + 'T00:00:00');
    const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0)  return { status: 'expired',  diffDays, label: 'Expired',          color: 'red'    };
    if (diffDays <= 30) return { status: 'expiring', diffDays, label: `${diffDays}d left`, color: 'orange' };
    return               { status: 'active',   diffDays, label: raw,                color: 'green'  };
  })();

  const [showNewModal, setShowNewModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showFindReplaceModal, setShowFindReplaceModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showExportFormatModal, setShowExportFormatModal] = useState(false);
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [showCaseConverterModal, setShowCaseConverterModal] = useState(false);
  const [showValidatorModal, setShowValidatorModal] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showDomainsModal, setShowDomainsModal] = useState(false);
  const [showUserTypesModal, setShowUserTypesModal] = useState(false);
  const [showModelBusinessRulesModal, setShowModelBusinessRulesModal] = useState(false);
  const [showCreateVersionModal, setShowCreateVersionModal] = useState(false);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [versionCount, setVersionCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const { isOpen: confirmOpen, config: confirmConfig, confirm, cancel } = useConfirm();

  // Load user-defined types once when authenticated
  useEffect(() => {
    if (!user) return;
    userTypeApi.getAll()
      .then(types => setUserTypes(types))
      .catch(() => {}); // silent — not critical for app function
  }, [user]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z or Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo() && modelName) {
          undo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, canUndo, modelName]);

  const handleSave = async () => {
    const modelData = getModelData();

    if (!modelData.name) {
      toast.error('Please enter a model name');
      return;
    }

    logger.log('=== SAVING MODEL ===', { name: modelData.name, nodeCount: modelData.nodes?.length });

    setIsSaving(true);
    try {
      const activeModelId = currentModel?._id || currentModel?.id;
      let response;
      if (activeModelId) {
        response = await modelApi.update(activeModelId, modelData);
        toast.success('Model saved successfully!');
      } else {
        response = await modelApi.create(modelData);
        toast.success('Model created successfully!');
      }

      const savedData = response?.data !== undefined ? response.data : response;
      const modelId = savedData?._id || savedData?.id || activeModelId;

      const mergedModel = {
        ...currentModel,
        ...savedData,
        _id: modelId,
        id: modelId,
        nodes: savedData?.nodes || savedData?.data?.nodes || modelData.nodes,
        edges: savedData?.edges || savedData?.data?.edges || modelData.edges,
        viewport: savedData?.viewport || savedData?.data?.viewport || modelData.viewport,
      };

      setCurrentModel(mergedModel);
      setIsDirty(false);

      const state = useFlowStore.getState();
      state.isLoading = true;
      setTimeout(() => {
        const currentState = useFlowStore.getState();
        currentState.isLoading = false;
      }, 500);
    } catch (error) {
      toast.error(`Failed to save model: ${error.message}`);
      logger.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = () => {
    // Check if there's an active model
    if (!modelName || modelName.trim() === '') {
      toast.error('Please create or open a model before exporting');
      return;
    }
    
    // Show format selection modal
    setShowExportFormatModal(true);
  };

  const handleExportWithFormat = async (format) => {
    try {
      // Close modal
      setShowExportFormatModal(false);
      
      // Get current model data (includes userTypes from store via getModelData)
      const modelData = getModelData();
      
      
      if (format === 'xmi') {
        // Export to XMI format
        downloadXMI(modelData, `${modelData.name || 'model'}.xmi`);
        toast.success('Model exported to XMI format!');
      } else if (format === 'sql') {
        // Export to SQL DDL format
        downloadSQL(modelData, `${modelData.name || 'model'}.sql`);
        toast.success('Model exported to SQL DDL format!');
      } else if (format === 'mermaid') {
        // Export to Mermaid ERD format
        downloadMermaid(modelData, `${modelData.name || 'model'}.mmd`);
        toast.success('Model exported to Mermaid ERD format!');
      } else if (format === 'json') {
        // Export to JSON format (legacy)
        downloadJSON(modelData, `${modelData.name || 'model'}.json`);
      } else if (format === 'dictionary') {
        // Export Data Dictionary
        handleExportDataDictionary();
        return;
      } else if (format === 'png' || format === 'jpeg' || format === 'svg-image') {
        // Export as image — use nodes from Zustand store (no useReactFlow needed)
        const name = modelData.name || 'model';
        const toastId = toast.loading('Generating image...');
        try {
          let filename;
          if (format === 'png') {
            filename = await exportToPng(nodes, name);
          } else if (format === 'jpeg') {
            filename = await exportToJpeg(nodes, name);
          } else {
            filename = await exportToSvgImage(nodes, name);
          }
          toast.success(`Exported as ${filename}`, { id: toastId });
        } catch (imgError) {
          toast.error(`Image export failed: ${imgError.message}`, { id: toastId });
        }
        return;
      }
    } catch (error) {
      logger.error('Export error:', error);
      toast.error(`Failed to export: ${error.message}`);
    }
  };

  const handleImport = () => {
    // Check if there's an active model
    if (!modelName || modelName.trim() === '') {
      toast.error('Please create or open a model before importing');
      return;
    }

    setShowImportModal(true);
  };

  const handleImportUnified = async (importType, file) => {
    try {
      logger.log('=== IMPORTING MODEL ===', { type: importType, file: file.name });

      if (importType === 'sql') {
        // Handle SQL import
        const sqlContent = await file.text();
        logger.log('[SQL Import] File:', file.name, sqlContent.length, 'bytes');

        // Parse SQL DDL
        const { tables, views, relationships } = parseSQLDDL(sqlContent);
        logger.log('[SQL Import] Parse result:', { 
          tables: tables.length, 
          views: views.length, 
          relationships: relationships.length 
        });

        if (tables.length === 0 && views.length === 0) {
          toast.error('No tables or views found in SQL file');
          return;
        }

        // Create nodes for tables
        const nodeMap = {};
        let xPos = 100;
        let yPos = 100;
        const spacing = 300;
        const itemsPerRow = 4;
        let itemsInRow = 0;

        // Create table nodes
        for (const table of tables) {
          const nodeId = generateId();
          const node = {
            id: nodeId,
            type: 'table',
            position: { x: xPos, y: yPos },
            data: {
              name: table.name,
              schema: table.schema || '',
              description: table.description,
              fields: table.fields,
              color: null,
              tags: []
            }
          };

          addNode(node);
          nodeMap[table.name] = nodeId;

          // Position calculation
          itemsInRow++;
          if (itemsInRow >= itemsPerRow) {
            xPos = 100;
            yPos += spacing;
            itemsInRow = 0;
          } else {
            xPos += spacing;
          }
        }

        // Create view nodes
        for (const view of views) {
          const nodeId = generateId();
          const node = {
            id: nodeId,
            type: 'view',
            position: { x: xPos, y: yPos },
            data: {
              name: view.name,
              schema: view.schema || '',
              description: view.description,
              sqlDefinition: view.sqlDefinition,
              fields: [],
              color: null,
              tags: []
            }
          };

          addNode(node);
          nodeMap[view.name] = nodeId;

          // Position calculation
          itemsInRow++;
          if (itemsInRow >= itemsPerRow) {
            xPos = 100;
            yPos += spacing;
            itemsInRow = 0;
          } else {
            xPos += spacing;
          }
        }

        // Create relationships
        for (const rel of relationships) {
          const sourceId = nodeMap[rel.sourceTable];
          const targetId = nodeMap[rel.targetTable];

          if (sourceId && targetId) {
            // Mark field as foreign key
            const sourceNode = nodes.find(n => n.id === sourceId);
            if (sourceNode) {
              const field = sourceNode.data.fields.find(f => f.name === rel.sourceField);
              if (field) {
                field.isForeignKey = true;
              }
            }

            // Create edge directly with locked cardinality
            createEdgeDirectly(
              {
                source: sourceId,
                target: targetId,
                sourceHandle: null,
                targetHandle: null,
              },
              'N:1', // Default cardinality for SQL FK (many-to-one)
              'FK'   // Default label
            );
          }
        }

        setIsDirty(true);
        const entityCount = tables.length + views.length;
        toast.success(`Imported ${entityCount} entities and ${relationships.length} relationships from SQL`);

      } else {
        // Handle XMI/JSON import
        let modelData;

        if (importType === 'xmi') {
          // Import XMI file
          const xmiContent = await file.text();
          logger.log('XMI content length:', xmiContent.length);
          
          // Validate XMI
          if (!validateXMI(xmiContent)) {
            toast.error('Invalid XMI file format');
            return;
          }
          
          // Parse XMI to internal format
          modelData = importFromXMI(xmiContent);
          logger.log('Parsed XMI to model data:', {
            name: modelData.name,
            nodesCount: modelData.nodes?.length,
            edgesCount: modelData.edges?.length
          });
          
          // Validate parsed data structure
          if (!modelData.nodes || !Array.isArray(modelData.nodes)) {
            logger.error('Invalid model data structure:', modelData);
            toast.error('XMI file structure is invalid');
            return;
          }
        } else {
          // Import JSON file
          modelData = await parseJSONFile(file);
          logger.log('Parsed JSON:', { nodes: modelData.nodes?.length, edges: modelData.edges?.length });
          
          // Frontend validation for JSON
          const errors = validateModelData(modelData);
          if (errors.length > 0) {
            logger.error('Frontend validation errors:', errors);
            toast.error(`Invalid model file: ${errors.join(', ')}`);
            return;
          }
        }

        // Add nodes and edges to current model (same as SQL import)
        const nodeIdMap = {};
        let xPos = 100;
        let yPos = 100;
        const spacing = 300;
        const itemsPerRow = 4;
        let itemsInRow = 0;

        // Add all nodes from imported file
        for (const node of modelData.nodes || []) {
          const nodeId = generateId();
          const newNode = {
            id: nodeId,
            type: node.type || 'table',
            position: node.position || { x: xPos, y: yPos },
            data: {
              name: node.data?.name || 'Unnamed',
              description: node.data?.description || '',
              fields: node.data?.fields || [],
              color: node.data?.color || null,
              tags: node.data?.tags || [],
              businessRules: node.data?.businessRules || null,
              sqlDefinition: node.data?.sqlDefinition || null,
              ...(node.data || {})
            }
          };

          addNode(newNode);
          nodeIdMap[node.id] = nodeId;

          // Position calculation (only if node doesn't have explicit position)
          if (!node.position) {
            itemsInRow++;
            if (itemsInRow >= itemsPerRow) {
              xPos = 100;
              yPos += spacing;
              itemsInRow = 0;
            } else {
              xPos += spacing;
            }
          }
        }

        // Add all edges from imported file
        for (const edge of modelData.edges || []) {
          const newSourceId = nodeIdMap[edge.source];
          const newTargetId = nodeIdMap[edge.target];

          if (newSourceId && newTargetId) {
            createEdgeDirectly({
              source: newSourceId,
              target: newTargetId,
              sourceHandle: edge.sourceHandle || null,
              targetHandle: edge.targetHandle || null,
              data: {
                label: edge.data?.label || '',
                cardinality: edge.data?.cardinality || '1:N',
                cardinalityLocked: edge.data?.cardinalityLocked !== undefined ? edge.data.cardinalityLocked : true,
                ...(edge.data || {})
              }
            });
          }
        }

        // Merge global tags if present
        if (modelData.globalTags && Array.isArray(modelData.globalTags)) {
          const currentStore = useFlowStore.getState();
          const existingTags = currentStore.globalTags || [];
          
          // Add new tags that don't exist yet
          for (const tag of modelData.globalTags) {
            const tagExists = existingTags.some(t => 
              t.name === tag.name && t.color === tag.color
            );
            if (!tagExists) {
              currentStore.addGlobalTag(tag);
            }
          }
        }

        // Apply model-level business rules if present in the imported file
        if (modelData.businessRules?.trim()) {
          const currentRules = useFlowStore.getState().modelBusinessRules || '';
          if (!currentRules.trim()) {
            // Current model has no rules — apply imported rules directly
            setModelBusinessRules(modelData.businessRules.trim());
          } else {
            // Both have rules — append imported rules with a separator
            setModelBusinessRules(
              `${currentRules.trim()}\n\n---\n\n${modelData.businessRules.trim()}`
            );
          }
        }

        setIsDirty(true);
        const entityCount = modelData.nodes?.length || 0;
        const relCount = modelData.edges?.length || 0;
        toast.success(`Imported ${entityCount} entities and ${relCount} relationships from ${importType.toUpperCase()}`);
      }
    } catch (error) {
      logger.error('Import error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      
      // More detailed error message
      let errorMsg = error.message;
      if (error.response?.data?.errors) {
        errorMsg = error.response.data.errors.map(e => e.msg).join(', ');
      }
      
      toast.error(`Failed to import: ${errorMsg}`);
    }
  };


  const handleExportDataDictionary = () => {
    if (!modelName || modelName.trim() === '') {
      toast.error('Please create or open a model before exporting data dictionary');
      return;
    }

    if (nodes.length === 0) {
      toast.error('No entities to document');
      return;
    }

    try {
      // Generate Markdown content
      let markdown = `# Data Dictionary: ${modelName}\n\n`;
      
      if (modelType) {
        markdown += `**Model Type:** ${modelType.charAt(0).toUpperCase() + modelType.slice(1)}\n\n`;
      }

      const modelData = getModelData();
      if (modelData.description) {
        markdown += `**Description:** ${modelData.description}\n\n`;
      }

      markdown += `**Generated:** ${new Date().toLocaleString()}\n\n`;
      markdown += `---\n\n`;

      // Model Business Rules — before Summary
      if (modelBusinessRules?.trim()) {
        markdown += `## Business Rules\n\n`;
        markdown += `${modelBusinessRules.trim()}\n\n`;
        markdown += `---\n\n`;
      }

      // Count entities by type
      const entityCounts = nodes.reduce((acc, node) => {
        acc[node.type] = (acc[node.type] || 0) + 1;
        return acc;
      }, {});

      markdown += `## Summary\n\n`;
      markdown += `- **Total Entities:** ${nodes.length}\n`;
      Object.entries(entityCounts).forEach(([type, count]) => {
        markdown += `  - ${type}: ${count}\n`;
      });
      markdown += `- **Total Relationships:** ${edges.length}\n\n`;
      markdown += `---\n\n`;

      // User-Defined Types section
      const usedUdtNames = new Set();
      nodes.forEach(node => {
        (node.data?.fields || []).forEach(f => {
          if (f.type?.startsWith('UDT:')) usedUdtNames.add(f.type.slice(4));
        });
      });
      if (usedUdtNames.size > 0 && userTypes.length > 0) {
        markdown += `## User-Defined Types\n\n`;
        markdown += `| Name | Base Type | Size | Fill Rule | Validation Rule |\n`;
        markdown += `|------|-----------|------|-----------|----------------|\n`;
        usedUdtNames.forEach(name => {
          const udt = userTypes.find(u => u.name === name);
          if (udt) {
            markdown += `| UDT:${udt.name} | ${udt.baseType} | ${udt.size ?? ''} | ${udt.fillRule || ''} | ${udt.validationRule || ''} |\n`;
          }
        });
        markdown += `\n---\n\n`;
      }

      // Group nodes by type
      const nodesByType = nodes.reduce((acc, node) => {
        if (!acc[node.type]) acc[node.type] = [];
        acc[node.type].push(node);
        return acc;
      }, {});

      // Document each entity type
      Object.entries(nodesByType).forEach(([type, typeNodes]) => {
        const typeLabel = type === 'table' ? 'Tables' :
                         type === 'collection' ? 'Collections' :
                         type === 'fact' ? 'Fact Tables' :
                         type === 'dimension' ? 'Dimension Tables' :
                         type === 'view' ? 'Views' :
                         type === 'text' ? 'Text Notes' : type;

        markdown += `## ${typeLabel}\n\n`;

        typeNodes.sort((a, b) => (a.data.name || '').localeCompare(b.data.name || ''));

        typeNodes.forEach((node) => {
          markdown += `### ${node.data.name || 'Unnamed'}\n\n`;

          // Description
          if (node.data.description) {
            markdown += `**Description:** ${node.data.description}\n\n`;
          }

          // Business Rules (Entity-level)
          if (node.data.businessRules?.entity) {
            markdown += `#### Business Rules\n\n`;
            markdown += `${node.data.businessRules.entity}\n\n`;
          }

          // Tags
          if (node.data.tags && node.data.tags.length > 0) {
            markdown += `**Tags:** `;
            markdown += node.data.tags.map(tag => `\`${tag.text}\``).join(', ');
            markdown += `\n\n`;
          }

          // SQL Definition for Views
          if (node.type === 'view' && node.data.sqlDefinition) {
            markdown += `**SQL Definition:**\n\`\`\`sql\n${node.data.sqlDefinition}\n\`\`\`\n\n`;
          }

          // Text Note Content
          if (node.type === 'text' && node.data.content) {
            markdown += `**Content:**\n\n${node.data.content}\n\n`;
          }

          // Fields
          if (node.data.fields && node.data.fields.length > 0) {
            markdown += `#### Fields\n\n`;
            markdown += `| Field Name | Type | Nullable | PK | Auto | FK | Check Constraint | Description |\n`;
            markdown += `|------------|------|----------|----|------|----|------------------|-------------|\n`;

            node.data.fields.forEach(field => {
              const nullable = field.nullable === false ? '❌' : '✅';
              const isPK = field.isPrimaryKey ? '✅' : '';
              const isAuto = field.autoIncrement ? '✅' : '';
              const isFK = field.isForeignKey ? '✅' : '';
              const checkConstraint = field.checkConstraint ? `\`${field.checkConstraint}\`` : '';
              const description = field.description || '';
              // Show base type for UDTs, keeping UDT label
              let typeLabel = field.type;
              if (field.type?.startsWith('UDT:')) {
                const udtName = field.type.slice(4);
                const udt = userTypes.find(u => u.name === udtName);
                if (udt) typeLabel = `UDT:${udt.name} (${udt.baseType}${udt.size ? `(${udt.size})` : ''})`;
              }
              markdown += `| ${field.name} | ${typeLabel} | ${nullable} | ${isPK} | ${isAuto} | ${isFK} | ${checkConstraint} | ${description} |\n`;
            });

            markdown += `\n`;

            // Field-level Business Rules
            const fieldRules = node.data.businessRules?.fields || {};
            const fieldsWithRules = node.data.fields.filter(field => fieldRules[field.name]);
            
            if (fieldsWithRules.length > 0) {
              markdown += `##### Field Business Rules\n\n`;
              fieldsWithRules.forEach(field => {
                markdown += `**${field.name}:**\n\n`;
                markdown += `${fieldRules[field.name]}\n\n`;
              });
            }
          }

          // Indexes
          if (node.data.indexes && node.data.indexes.length > 0) {
            markdown += `#### Indexes\n\n`;
            markdown += `| Index Name | Type | Columns |\n`;
            markdown += `|------------|------|----------|\n`;

            node.data.indexes.forEach(index => {
              const columns = index.columns.map(col => `${col.fieldName} (${col.order})`).join(', ');
              markdown += `| ${index.name} | ${index.type} | ${columns} |\n`;
            });

            markdown += `\n`;
          }

          // Partitioning
          const partitioning = node.data?.partitioning;
          if (partitioning && partitioning.type && partitioning.type !== 'none') {
            markdown += `#### Partitioning\n\n`;
            markdown += `- **Strategy:** ${partitioning.type.toUpperCase()}\n`;
            if (partitioning.type === 'range' || partitioning.type === 'list') {
              markdown += `- **Key Column:** \`${partitioning.column || 'N/A'}\`\n`;
              const parts = partitioning.partitions || [];
              if (parts.length > 0) {
                if (partitioning.type === 'range') {
                  markdown += `\n| Partition Name | Start (From) | End (To / Less Than) |\n`;
                  markdown += `|----------------|--------------|----------------------|\n`;
                  parts.forEach(p => {
                    markdown += `| ${p.name || 'unnamed'} | ${p.from || 'MINVALUE'} | ${p.to || 'MAXVALUE'} |\n`;
                  });
                } else if (partitioning.type === 'list') {
                  markdown += `\n| Partition Name | Values List |\n`;
                  markdown += `|----------------|-------------|\n`;
                  parts.forEach(p => {
                    markdown += `| ${p.name || 'unnamed'} | ${p.values || 'DEFAULT'} |\n`;
                  });
                }
                markdown += `\n`;
              }
            } else if (partitioning.type === 'hash') {
              const hashCols = (partitioning.columns && partitioning.columns.length > 0)
                ? partitioning.columns.map(c => `\`${c}\``).join(', ')
                : 'N/A';
              markdown += `- **Hash Key Columns:** ${hashCols}\n`;
              markdown += `- **Buckets Count:** ${partitioning.hashPartitionsCount || 4}\n\n`;
            }
          }

          // Relationships (outgoing)
          const outgoingEdges = edges.filter(e => e.source === node.id);
          if (outgoingEdges.length > 0) {
            markdown += `#### Relationships (Outgoing)\n\n`;
            outgoingEdges.forEach(edge => {
              const targetNode = nodes.find(n => n.id === edge.target);
              if (targetNode) {
                const label = edge.data?.label || 'relates to';
                const cardinality = edge.data?.cardinality || '';
                const sourceField = edge.data?.sourceField || '';
                const targetField = edge.data?.targetField || '';
                
                markdown += `- **${label}** → \`${targetNode.data.name}\``;
                if (cardinality) markdown += ` (${cardinality})`;
                if (sourceField && targetField) markdown += ` [${sourceField} → ${targetField}]`;
                markdown += `\n`;
              }
            });
            markdown += `\n`;
          }

          // Relationships (incoming)
          const incomingEdges = edges.filter(e => e.target === node.id);
          if (incomingEdges.length > 0) {
            markdown += `#### Relationships (Incoming)\n\n`;
            incomingEdges.forEach(edge => {
              const sourceNode = nodes.find(n => n.id === edge.source);
              if (sourceNode) {
                const label = edge.data?.label || 'relates to';
                const cardinality = edge.data?.cardinality || '';
                const sourceField = edge.data?.sourceField || '';
                const targetField = edge.data?.targetField || '';
                
                markdown += `- \`${sourceNode.data.name}\` **${label}** ←`;
                if (cardinality) markdown += ` (${cardinality})`;
                if (sourceField && targetField) markdown += ` [${sourceField} → ${targetField}]`;
                markdown += `\n`;
              }
            });
            markdown += `\n`;
          }

          markdown += `---\n\n`;
        });
      });

      // Relationship Summary
      if (edges.length > 0) {
        markdown += `## Relationships Summary\n\n`;
        markdown += `| Source | Relationship | Target | Cardinality |\n`;
        markdown += `|--------|--------------|--------|-------------|\n`;

        edges.forEach(edge => {
          const sourceNode = nodes.find(n => n.id === edge.source);
          const targetNode = nodes.find(n => n.id === edge.target);
          const label = edge.data?.label || 'relates to';
          const cardinality = edge.data?.cardinality || 'N/A';

          if (sourceNode && targetNode) {
            markdown += `| ${sourceNode.data.name} | ${label} | ${targetNode.data.name} | ${cardinality} |\n`;
          }
        });

        markdown += `\n`;
      }

      // Junction Tables (N:N Relationships)
      const junctionTables = edges.filter(e => e.data?.cardinality === 'N:N' && e.data?.junctionTable);
      if (junctionTables.length > 0) {
        markdown += `## Junction Tables (N:N Relationships)\n\n`;
        markdown += `Junction tables are automatically created for many-to-many relationships.\n\n`;

        junctionTables.forEach(edge => {
          const junctionTable = edge.data.junctionTable;
          const sourceNode = nodes.find(n => n.id === edge.source);
          const targetNode = nodes.find(n => n.id === edge.target);
          
          markdown += `### ${junctionTable.name}\n\n`;
          
          if (junctionTable.description) {
            markdown += `**Description:** ${junctionTable.description}\n\n`;
          }
          
          if (sourceNode && targetNode) {
            markdown += `**Connects:** \`${sourceNode.data.name}\` ↔ \`${targetNode.data.name}\`\n\n`;
          }

          // Fields
          if (junctionTable.fields && junctionTable.fields.length > 0) {
            markdown += `#### Fields\n\n`;
            markdown += `| Field Name | Type | Nullable | PK | Auto | FK | Description |\n`;
            markdown += `|------------|------|----------|----|------|----|---------|\n`;

            junctionTable.fields.forEach(field => {
              const nullable = field.nullable === false ? '❌' : '✅';
              const isPK = field.isPrimaryKey ? '✅' : '';
              const isAuto = field.autoIncrement ? '✅' : '';
              const isFK = field.isForeignKey ? '✅' : '';
              const description = field.description || '';
              
              markdown += `| ${field.name} | ${field.type} | ${nullable} | ${isPK} | ${isAuto} | ${isFK} | ${description} |\n`;
            });

            markdown += `\n`;
          }

          // Indexes
          if (junctionTable.indexes && junctionTable.indexes.length > 0) {
            markdown += `#### Indexes\n\n`;
            markdown += `| Index Name | Type | Columns |\n`;
            markdown += `|------------|------|----------|\n`;

            junctionTable.indexes.forEach(index => {
              const columns = index.columns.map(col => `${col.fieldName} (${col.order})`).join(', ');
              markdown += `| ${index.name} | ${index.type} | ${columns} |\n`;
            });

            markdown += `\n`;
          }

          markdown += `---\n\n`;
        });
      }

      // Download the file
      const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${modelName.replace(/[^a-z0-9]/gi, '_')}_data_dictionary.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Data dictionary exported successfully!');
    } catch (error) {
      logger.error('Error exporting data dictionary:', error);
      toast.error('Failed to export data dictionary');
    }
  };

  const handleAutoLayout = async () => {
    if (!modelName || modelName.trim() === '') {
      toast.error('Please create or open a model before organizing');
      return;
    }

    if (nodes.length === 0) {
      toast.error('No entities to organize');
      return;
    }

    // Show layout algorithm selection modal
    setShowLayoutModal(true);
  };

  const handleLayoutConfirm = (algorithmId) => {
    setShowLayoutModal(false);

    try {
      // Apply the selected layout algorithm
      const updatedNodes = applyLayout(algorithmId, nodes, edges);
      
      setNodes(updatedNodes);
      setIsDirty(true);
      
      // Get algorithm name for toast
      const algorithmNames = {
        hierarchical: 'Hierarchical (BFS)',
        sugiyama: 'Sugiyama (Layered)',
        force: 'Force-Directed',
        grid: 'Grid',
        circular: 'Circular',
        radial: 'Radial Tree'
      };
      
      toast.success(`Organized ${nodes.length} entities using ${algorithmNames[algorithmId] || algorithmId} layout!`);
    } catch (error) {
      logger.error('Error organizing entities:', error);
      toast.error('Failed to organize entities');
    }
  };

  const handleCaseConverter = () => {
    if (!modelName || modelName.trim() === '') {
      toast.error('Please create or open a model before converting names');
      return;
    }

    if (nodes.length === 0) {
      toast.error('No entities to convert');
      return;
    }

    setShowCaseConverterModal(true);
  };

  const handleCaseConversion = ({ caseType, applyTo, scope, preview }) => {
    setShowCaseConverterModal(false);

    try {
      const updatedNodes = nodes.map(node => {
        const change = preview.find(c => c.nodeId === node.id);
        if (!change) return node;

        const updatedNode = { ...node };

        // Update entity name
        if (change.newName !== change.originalName) {
          updatedNode.data = {
            ...updatedNode.data,
            name: change.newName
          };
        }

        // Update field names
        if (change.fields.length > 0) {
          updatedNode.data = {
            ...updatedNode.data,
            fields: (updatedNode.data?.fields || []).map(field => {
              const fieldChange = change.fields.find(f => f.original === field.name);
              if (fieldChange) {
                return {
                  ...field,
                  name: fieldChange.new
                };
              }
              return field;
            })
          };
        }

        return updatedNode;
      });

      setNodes(updatedNodes);
      setIsDirty(true);

      const caseNames = {
        snake: 'snake_case',
        camel: 'camelCase',
        pascal: 'PascalCase',
        kebab: 'kebab-case',
        upper: 'UPPERCASE',
        lower: 'lowercase'
      };

      const totalChanges = preview.reduce((sum, node) => {
        let count = 0;
        if (node.newName !== node.originalName) count++;
        count += node.fields.length;
        return sum + count;
      }, 0);

      toast.success(`Applied ${caseNames[caseType]} to ${totalChanges} ${totalChanges === 1 ? 'item' : 'items'}!`);
    } catch (error) {
      logger.error('Error converting case:', error);
      toast.error('Failed to convert naming convention');
    }
  };

  const handleValidateModel = () => {
    if (!modelName || modelName.trim() === '') {
      toast.error('Please create or open a model before validating');
      return;
    }

    if (nodes.length === 0) {
      toast.error('No entities to validate');
      return;
    }

    try {
      const result = validateModel(nodes, edges);
      setValidationResult(result);
      setShowValidatorModal(true);
    } catch (error) {
      logger.error('Validation error:', error.message);
      toast.error(`Failed to validate model: ${error.message}`);
    }
  };

  const handleSelectEntityFromValidator = (entityId) => {
    const entity = nodes.find(n => n.id === entityId);
    if (entity) {
      setSelectedNode(entity);
    }
  };

  const handleInsertDomain = (domain) => {
    try {
      if (domain.type === 'table') {
        // Insert table domain as a new entity
        const newId = generateId();
        const newNode = {
          id: newId,
          type: domain.nodeType || 'table',
          position: {
            x: Math.random() * 400,
            y: Math.random() * 400
          },
          data: {
            name: domain.name,
            fields: domain.fields || [],
            indexes: domain.indexes || [], // Insert indexes from domain
            tags: domain.tags || [],
            domains: (() => {
              // Integrate template domains into the current model's global domain library
              return (domain.entityDomains || []).map(td => {
                const existing = globalDomains.find(
                  gd => gd.text.toLowerCase() === td.text.toLowerCase()
                );
                if (existing) {
                  return { id: generateId(), text: existing.text, globalDomainId: existing.id };
                }
                const newGlobalId = generateId();
                addGlobalDomain({ id: newGlobalId, text: td.text });
                return { id: generateId(), text: td.text, globalDomainId: newGlobalId };
              });
            })(),
            color: domain.color,
            description: domain.entityDescription || '', // Entity description
            sqlDefinition: domain.sqlDefinition,
            materialized: domain.materialized || false, // For materialized views
            businessRules: domain.businessRules || null // Business rules
          }
        };
        addNode(newNode);
        toast.success(`Entity "${domain.name}" inserted successfully!`);
      } else if (domain.type === 'field' && selectedNode) {
        // Insert field domain into selected entity
        const updatedFields = [...(selectedNode.data.fields || []), domain.field];
        updateNode(selectedNode.id, { ...selectedNode.data, fields: updatedFields });
        toast.success(`Field "${domain.field.name}" inserted successfully!`);
      } else if (domain.type === 'field' && !selectedNode) {
        toast.error('Please select an entity first to insert a field');
      }
    } catch (error) {
      logger.error('Error inserting domain:', error);
      toast.error('Failed to insert domain');
    }
  };

  const handleCreateVersion = async (description) => {
    if (!currentModel?._id) {
      toast.error('Please save the model first before creating a version');
      return;
    }

    try {
      const modelData = getModelData();
      
      const versionData = {
        modelId: currentModel._id,
        description,
        snapshot: {
          name: modelData.name,
          type: modelData.type,
          description: modelData.description,
          nodes: modelData.nodes,
          edges: modelData.edges,
          viewport: modelData.viewport,
          globalTags: modelData.globalTags,
          globalDomains: modelData.globalDomains
        }
      };

      await versionApi.create(versionData);
      toast.success('Version created successfully!');
      
      // Update version count
      const versions = await versionApi.getByModelId(currentModel._id);
      setVersionCount(versions.length);
    } catch (error) {
      logger.error('Error creating version:', error);
      toast.error('Failed to create version');
      throw error;
    }
  };

  const handleRestoreVersion = async (versionId) => {
    if (!currentModel?._id) {
      toast.error('No model loaded');
      return;
    }

    try {
      // Get current model state
      const currentModelData = getModelData();
      
      // Call restore endpoint (will save current state as new version)
      const response = await versionApi.restore(versionId, {
        currentSnapshot: {
          name: currentModelData.name,
          type: currentModelData.type,
          description: currentModelData.description,
          nodes: currentModelData.nodes,
          edges: currentModelData.edges,
          viewport: currentModelData.viewport,
          globalTags: currentModelData.globalTags
        },
        restoreDescription: `Auto-saved before restoring version`
      });

      // Load the restored snapshot
      const restoredModel = {
        ...currentModel,
        ...response.snapshot
      };
      
      setCurrentModel(restoredModel);
      toast.success(`Version #${response.versionNumber} restored successfully!`);
      
      // Update version count
      const versions = await versionApi.getByModelId(currentModel._id);
      setVersionCount(versions.length);
    } catch (error) {
      logger.error('Error restoring version:', error);
      toast.error('Failed to restore version');
      throw error;
    }
  };

  const loadVersionCount = async () => {
    if (currentModel?._id) {
      try {
        const versions = await versionApi.getByModelId(currentModel._id);
        setVersionCount(versions.length);
      } catch (error) {
        logger.error('Error loading version count:', error);
      }
    } else {
      setVersionCount(0);
    }
  };

  // Load version count when model changes
  useEffect(() => {
    loadVersionCount();
  }, [currentModel?._id]);

  return (
    <>
      <div className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4 shadow-sm">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          <button
            onClick={onToggleSidebar}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors lg:hidden text-gray-700 dark:text-gray-200"
            title="Toggle Sidebar"
          >
            <Menu size={20} />
          </button>

          <div className="flex items-center gap-2">
            <img 
              src={schemaGraphosLogo} 
              alt="Schema Graphos Logo" 
              className="h-8 w-8 object-contain"
            />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white hidden sm:block">
              Schema Graphos
            </h1>
          </div>

          {modelName && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-400">Model:</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {modelName}
              </span>
              {isDirty && (
                <span className="w-2 h-2 bg-orange-500 rounded-full" title="Unsaved changes" />
              )}
            </div>
          )}
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          {/* New Model */}
          <button
            onClick={() => setShowNewModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title="New Model"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">New</span>
          </button>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={isSaving || !modelName || !userHasRole}
            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !userHasRole 
                ? "You need an active subscription to save. Contact support to activate." 
                : !modelName 
                  ? "Create or open a model first" 
                  : "Save Model"
            }
          >
            <Save size={18} />
            <span className="hidden sm:inline">{isSaving ? 'Saving...' : 'Save'}</span>
          </button>

          {/* Undo */}
          <button
            onClick={undo}
            disabled={!canUndo() || !modelName}
            className={`p-2 rounded-lg transition-colors ${
              !canUndo() || !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={modelName ? "Undo (Ctrl+Z)" : "Create or open a model first"}
          >
            <Undo size={20} />
          </button>

          {/* Search */}
          <button
            onClick={onToggleSearch}
            disabled={!modelName}
            className={`p-2 rounded-lg transition-colors ${
              !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={modelName ? "Search Entities (Ctrl+K)" : "Create or open a model first"}
          >
            <Search size={20} />
          </button>

          {/* Find & Replace */}
          <button
            onClick={() => setShowFindReplaceModal(true)}
            disabled={!modelName}
            className={`p-2 rounded-lg transition-colors ${
              !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={modelName ? "Find & Replace Fields" : "Create or open a model first"}
          >
            <Replace size={20} />
          </button>

          {/* Filter Diagram Entities */}
          <button
            onClick={() => setShowFilterModal(true)}
            disabled={!modelName}
            className={`relative p-2 rounded-lg transition-colors ${
              !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : diagramFilter?.active
                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 font-bold ring-2 ring-blue-500'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={modelName ? "Filter Diagram Entities (by Type, Name, Tag, Domain)" : "Create or open a model first"}
          >
            <Filter size={20} />
            {diagramFilter?.active && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-600"></span>
              </span>
            )}
          </button>

          {/* Divider */}
          <div className="w-px h-8 bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Load */}
          <button
            onClick={() => setShowLoadModal(true)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-700 dark:text-gray-200"
            title="Load Model"
          >
            <FolderOpen size={20} />
          </button>

          {/* Export */}
          <button
            onClick={handleExport}
            disabled={!modelName}
            className={`p-2 rounded-lg transition-colors ${
              !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={modelName ? "Export Model (XMI/SQL/Mermaid/JSON/Dictionary)" : "Create or open a model first"}
          >
            <Download size={20} />
          </button>

          {/* Import */}
          <button
            onClick={handleImport}
            disabled={!modelName}
            className={`p-2 rounded-lg transition-colors ${
              !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={modelName ? "Import Model (XMI/JSON/SQL)" : "Create or open a model first"}
          >
            <Upload size={20} />
          </button>

          {/* Auto-Layout */}
          <button
            onClick={handleAutoLayout}
            disabled={!modelName}
            className={`p-2 rounded-lg transition-colors ${
              !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={modelName ? "Auto-Organize Entities" : "Create or open a model first"}
          >
            <Network size={20} />
          </button>

          {/* Case Converter */}
          <button
            onClick={handleCaseConverter}
            disabled={!modelName}
            className={`p-2 rounded-lg transition-colors ${
              !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={modelName ? "Convert Naming Convention (snake_case, camelCase, etc.)" : "Create or open a model first"}
          >
            <Type size={20} />
          </button>

          {/* Model Validator */}
          <button
            onClick={handleValidateModel}
            disabled={!modelName}
            className={`p-2 rounded-lg transition-colors ${
              !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={modelName ? "Validate Model Quality" : "Create or open a model first"}
          >
            <ShieldCheck size={20} />
          </button>

          {/* Objects Library */}
          <button
            onClick={() => setShowDomainsModal(true)}
            disabled={!modelName}
            className={`p-2 rounded-lg transition-colors ${
              !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={modelName ? "Objects Library (Reusable Templates)" : "Create or open a model first"}
          >
            <Library size={20} />
          </button>

          {/* Model Business Rules */}
          <button
            onClick={() => setShowModelBusinessRulesModal(true)}
            disabled={!modelName || !userHasRole}
            className={`p-2 rounded-lg transition-colors relative ${
              !modelName || !userHasRole
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={
              !userHasRole
                ? 'You need an active subscription to edit business rules'
                : !modelName
                ? 'Create or open a model first'
                : 'Model Business Rules'
            }
          >
            <BookText size={20} />
            {modelBusinessRules?.trim() && modelName && (
              <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-blue-500 rounded-full" />
            )}
          </button>

          {/* User-Defined Types */}
          <button
            onClick={() => setShowUserTypesModal(true)}
            disabled={!modelName || !userHasRole}
            className={`p-2 rounded-lg transition-colors ${
              !modelName || !userHasRole
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={
              !userHasRole
                ? 'You need an active subscription to manage user-defined types'
                : !modelName
                ? 'Create or open a model first'
                : 'User-Defined Types (Objects Library)'
            }
          >
            <Braces size={20} />
          </button>

          {/* Create Version */}
          <button
            onClick={() => setShowCreateVersionModal(true)}
            disabled={!modelName || !currentModel?._id || !userHasRole}
            className={`p-2 rounded-lg transition-colors ${
              !modelName || !currentModel?._id || !userHasRole
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={
              !userHasRole 
                ? "You need an active subscription to create versions. Contact support to activate."
                : modelName && currentModel?._id 
                  ? "Create Version Snapshot" 
                  : "Save model first to create versions"
            }
          >
            <GitCommit size={20} />
          </button>

          {/* Version History */}
          <button
            onClick={() => setShowVersionHistoryModal(true)}
            disabled={!currentModel?._id}
            className={`relative p-2 rounded-lg transition-colors ${
              !currentModel?._id
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={currentModel?._id ? `View Version History (${versionCount} versions)` : "Save model first to see version history"}
          >
            <GitBranch size={20} />
            {versionCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {versionCount > 9 ? '9+' : versionCount}
              </span>
            )}
          </button>

          {/* Divider */}
          <div className="w-px h-8 bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-700 dark:text-gray-200"
            title="Toggle Theme"
          >
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
          </button>

          {/* Field Descriptions Toggle */}
          <button
            onClick={toggleFieldDescriptions}
            disabled={!modelName}
            className={`p-2 rounded-lg transition-colors text-gray-700 dark:text-gray-200 ${
              !modelName
                ? 'opacity-50 cursor-not-allowed bg-gray-200 dark:bg-gray-700'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title={modelName ? (showFieldDescriptions ? "Hide field descriptions" : "Show field descriptions") : "Create or open a model first"}
          >
            {showFieldDescriptions ? <DescriptionsOn size={20} /> : <DescriptionsOff size={20} />}
          </button>

          {/* Multi-Select Mode Toggle */}
          <button
            onClick={toggleMultiSelectMode}
            disabled={!modelName}
            className={`p-2 rounded-lg transition-colors ${
              !modelName
                ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-600'
                : isMultiSelectMode
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title={modelName ? (isMultiSelectMode ? "Disable Multi-Select Mode (Enable Pan)" : "Enable Multi-Select Mode (Drag to select multiple entities)") : "Create or open a model first"}
          >
            {isMultiSelectMode ? <MousePointerClick size={20} /> : <Hand size={20} />}
          </button>

          {/* AI Assistant button — visible always; disabled when no model is open or license is expired */}
          {(() => {
            const modelIsOpen = !!(currentModel || modelName);
            const licenseExpired = licenseStatus?.status === 'expired';
            const isDisabled = !modelIsOpen || licenseExpired;
            const tooltip = licenseExpired
              ? 'License expired — AI Assistant unavailable'
              : !modelIsOpen
                ? 'Open or create a model to use the AI Assistant'
                : 'AI Assistant';
            return (
              <button
                onClick={() => setShowAiModal(true)}
                disabled={isDisabled}
                title={tooltip}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
                  isDisabled
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                <Bot size={18} />
                <span className="text-sm font-medium hidden lg:inline">AI</span>
              </button>
            );
          })()}

          {/* Divider */}
          <div className="w-px h-8 bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-700 dark:text-gray-200"
              title={user?.email || 'User Menu'}
            >
              <User size={18} />
              <span className="text-sm font-medium max-w-[120px] truncate">
                {user?.email?.split('@')[0] || 'User'}
              </span>
              {/* License badge in the toolbar button */}
              {licenseStatus && (
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                  licenseStatus.color === 'red'
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                    : licenseStatus.color === 'orange'
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                }`}>
                  {licenseStatus.label}
                </span>
              )}
              <ChevronDown size={14} className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <>
                {/* Backdrop to close menu */}
                <div 
                  className="fixed inset-0 z-[55]" 
                  onClick={() => setShowUserMenu(false)}
                />
                
                <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[60]">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user?.email}
                    </p>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {isAdmin && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                          ADMIN
                        </span>
                      )}
                      {/* License expiration info in dropdown */}
                      {licenseStatus && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${
                          licenseStatus.color === 'red'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            : licenseStatus.color === 'orange'
                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                            : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        }`}>
                          {licenseStatus.status === 'expired'  && '✕ License expired'}
                          {licenseStatus.status === 'expiring' && `⚠ Expires in ${licenseStatus.diffDays} day${licenseStatus.diffDays !== 1 ? 's' : ''}`}
                          {licenseStatus.status === 'active'   && `✓ License until ${user.expireDate}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    <a
                      href="https://help.schemagraphos.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <HelpCircle size={16} />
                      <span>Help / User Guide</span>
                    </a>
                    
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        setShowChangePasswordModal(true);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <KeyRound size={16} className="text-blue-500" />
                      <span>Change Password</span>
                    </button>

                    <div className="my-1 border-t border-gray-200 dark:border-gray-700" />

                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        setShowDeleteAccountModal(true);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors font-medium"
                    >
                      <UserX size={16} />
                      <span>Delete Account</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        logout();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <LogOut size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Divider */}
          <div className="w-px h-8 bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Properties Toggle */}
          <button
            onClick={onToggleProperties}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-700 dark:text-gray-200"
            title={showProperties ? "Hide Properties Panel" : "Show Properties Panel"}
          >
            {showProperties ? <PanelRightClose size={20} /> : <PanelRightOpen size={20} />}
          </button>
        </div>
      </div>

      {/* Modals */}
      {showNewModal && <NewModelModal onClose={() => setShowNewModal(false)} />}
      {showLoadModal && <LoadModelModal onClose={() => setShowLoadModal(false)} />}
      {showImportModal && (
        <ImportSelectorModal 
          onClose={() => setShowImportModal(false)}
          onSelectType={handleImportUnified}
        />
      )}
      {showFindReplaceModal && <FindReplaceModal isOpen={showFindReplaceModal} onClose={() => setShowFindReplaceModal(false)} />}
      <ExportFormatModal 
        isOpen={showExportFormatModal} 
        onClose={() => setShowExportFormatModal(false)} 
        onExport={handleExportWithFormat}
        modelName={modelName}
      />
      <LayoutAlgorithmModal
        isOpen={showLayoutModal}
        onClose={() => setShowLayoutModal(false)}
        onConfirm={handleLayoutConfirm}
        nodeCount={nodes.length}
      />
      <CaseConverterModal
        isOpen={showCaseConverterModal}
        onClose={() => setShowCaseConverterModal(false)}
        onConfirm={handleCaseConversion}
        nodes={nodes}
        selectedNode={selectedNode}
      />
      <ModelValidatorModal
        isOpen={showValidatorModal}
        onClose={() => setShowValidatorModal(false)}
        validationResult={validationResult}
        onSelectEntity={handleSelectEntityFromValidator}
      />

      {/* Objects Library Modal */}
      {showDomainsModal && (
        <DomainsLibraryModal
          onClose={() => setShowDomainsModal(false)}
          onInsert={handleInsertDomain}
          modelType={modelType}
        />
      )}

      {/* Model Business Rules Modal */}
      {showModelBusinessRulesModal && (
        <ModelBusinessRulesModal
          isOpen={showModelBusinessRulesModal}
          onClose={() => setShowModelBusinessRulesModal(false)}
          modelName={modelName}
          value={modelBusinessRules}
          onSave={(rules) => {
            setModelBusinessRules(rules);
          }}
        />
      )}

      {/* User-Defined Types Modal */}
      {showUserTypesModal && (
        <UserTypesModal
          onClose={() => setShowUserTypesModal(false)}
          onTypesChanged={(types) => setUserTypes(types)}
        />
      )}

      {/* Filter Entities Modal */}
      <FilterEntitiesModal isOpen={showFilterModal} onClose={() => setShowFilterModal(false)} />

      {/* Create Version Modal */}
      {showCreateVersionModal && (
        <CreateVersionModal
          onClose={() => setShowCreateVersionModal(false)}
          onSave={handleCreateVersion}
          currentVersionNumber={versionCount}
        />
      )}

      {/* Version History Modal */}
      {showVersionHistoryModal && (
        <VersionHistoryModal
          onClose={() => setShowVersionHistoryModal(false)}
          onRestore={handleRestoreVersion}
          modelId={currentModel?._id}
          currentNodes={nodes}
          currentEdges={edges}
        />
      )}
      
      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        type={confirmConfig.type}
        onConfirm={confirmConfig.onConfirm}
        onCancel={cancel}
      />

      {/* AI Assistant Modal */}
      <AiAssistantModal
        isOpen={showAiModal}
        onClose={() => setShowAiModal(false)}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
      />
    </>
  );
};

export default Toolbar;
